import { useState } from 'react';
import type React from 'react';
import {
  Loader2,
  Database,
  RefreshCw,
  Activity,
  Trash2,
  X,
} from 'lucide-react';
import type { ConfigDetail, QueueStatusData, QueueJob } from './types';
import { JobDetailModal } from './JobDetailModal';

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/* ─── Props ────────────────────────────────────── */

interface QueuesTabProps {
  detail: ConfigDetail;
  queueStatus: QueueStatusData | null;
  queueLoading: boolean;
  dailyQueueStatus: QueueStatusData | null;
  dailyQueueLoading: boolean;
  acting: string | null;
  onRefreshBackfill: () => void;
  onRefreshDaily: () => void;
  onRetryJob: (jobId: string, queue: string) => void;
  onRemoveJob: (jobId: string, queue: string) => void;
  onDrainQueue: (queue: string) => void;
  onRetryAllFailed: (queue: string) => void;
  onRemoveAllFailed: (queue: string) => void;
}

/* ─── QueuesTab ────────────────────────────────── */

export function QueuesTab({
  detail,
  queueStatus,
  queueLoading,
  dailyQueueStatus,
  dailyQueueLoading,
  acting,
  onRefreshBackfill,
  onRefreshDaily,
  onRetryJob,
  onRemoveJob,
  onDrainQueue,
  onRetryAllFailed,
  onRemoveAllFailed,
}: QueuesTabProps) {
  const [selectedJob, setSelectedJob] = useState<{ job: QueueJob; queue: string } | null>(null);

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Backfill Queue */}
      <QueueCard
        title="Backfill Queue"
        queueName="backfill"
        icon={Database}
        data={queueStatus}
        loading={queueLoading}
        acting={acting}
        onRefresh={onRefreshBackfill}
        onRetry={(id) => onRetryJob(id, 'backfill')}
        onRemove={(id) => onRemoveJob(id, 'backfill')}
        onDrain={() => onDrainQueue('backfill')}
        onRetryAll={() => onRetryAllFailed('backfill')}
        onRemoveAll={() => onRemoveAllFailed('backfill')}
        onSelectJob={(job) => setSelectedJob({ job, queue: 'backfill' })}
      />

      {/* Daily Pull Queue */}
      <DailyPullCard
        detail={detail}
        data={dailyQueueStatus}
        loading={dailyQueueLoading}
        acting={acting}
        onRefresh={onRefreshDaily}
        onRetry={(id) => onRetryJob(id, 'daily-pull')}
        onRemove={(id) => onRemoveJob(id, 'daily-pull')}
        onDrain={() => onDrainQueue('daily-pull')}
        onRetryAll={() => onRetryAllFailed('daily-pull')}
        onRemoveAll={() => onRemoveAllFailed('daily-pull')}
        onSelectJob={(job) => setSelectedJob({ job, queue: 'daily-pull' })}
      />

      {/* Job Detail Modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob.job}
          queueName={selectedJob.queue}
          acting={acting}
          onRetry={(id, q) => { onRetryJob(id, q); setSelectedJob(null); }}
          onRemove={(id, q) => { onRemoveJob(id, q); setSelectedJob(null); }}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </div>
  );
}

/* ─── Queue Count Row ──────────────────────────── */

function QueueCountRow({ counts }: { counts: QueueStatusData['counts'] }) {
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {(['waiting', 'active', 'completed', 'failed', 'delayed'] as const).map(status => {
        const colorMap = {
          waiting: 'text-amber-600 bg-amber-50 dark:bg-amber-950/40',
          active: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40',
          completed: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/40',
          failed: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40',
          delayed: 'text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-950',
        };
        return (
          <div key={status} className={`rounded-lg p-1.5 text-center ${colorMap[status]}`}>
            <div className="text-sm font-semibold">{counts[status]}</div>
            <div className="text-[9px] capitalize">{status}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Failed Job Row ───────────────────────────── */

function FailedJobRow({
  job,
  acting,
  onRetry,
  onRemove,
  onSelect,
}: {
  job: QueueJob;
  acting: string | null;
  onRetry: () => void;
  onRemove: () => void;
  onSelect: () => void;
}) {
  const attempts = job.attemptsMade ?? 0;
  const maxAttempts = job.maxAttempts ?? 1;
  const exhausted = attempts >= maxAttempts;

  return (
    <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-lg p-2">
      <div className="flex items-start justify-between gap-2">
        <button onClick={onSelect} className="min-w-0 text-left cursor-pointer hover:opacity-80">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-medium text-red-800 dark:text-red-400 truncate">{job.name}</span>
            <span className={`text-[8px] px-1 py-0.5 rounded font-medium shrink-0 ${
              exhausted ? 'bg-red-200 text-red-800' : 'bg-amber-100 text-amber-700'
            }`}>
              {attempts}/{maxAttempts}
            </span>
            {job.finishedOn && (
              <span className="text-[8px] text-red-400 shrink-0">{timeAgo(job.finishedOn)}</span>
            )}
          </div>
          <div className="text-[9px] text-red-600 dark:text-red-400 truncate mt-0.5">{job.failedReason}</div>
        </button>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={onRetry}
            disabled={acting === `retry-${job.id}`}
            className="px-1.5 py-0.5 text-[9px] font-medium bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900/50 text-red-700 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-950/40 disabled:opacity-50"
          >
            Retry
          </button>
          <button
            onClick={onRemove}
            disabled={acting === `remove-${job.id}`}
            className="px-1.5 py-0.5 text-[9px] font-medium bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Bulk Actions ─────────────────────────────── */

function BulkActions({
  count,
  acting,
  actingPrefix,
  onRetryAll,
  onRemoveAll,
}: {
  count: number;
  acting: string | null;
  actingPrefix: string;
  onRetryAll: () => void;
  onRemoveAll: () => void;
}) {
  if (count <= 1) return null;
  return (
    <div className="flex items-center gap-2 mb-1">
      <button
        onClick={onRetryAll}
        disabled={acting === `retry-all-${actingPrefix}`}
        className="text-[9px] text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-400 flex items-center gap-0.5 disabled:opacity-50"
      >
        {acting === `retry-all-${actingPrefix}` ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RefreshCw className="w-2.5 h-2.5" />}
        Retry All ({count})
      </button>
      <span className="text-gray-300">|</span>
      <button
        onClick={onRemoveAll}
        disabled={acting === `remove-all-${actingPrefix}`}
        className="text-[9px] text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex items-center gap-0.5 disabled:opacity-50"
      >
        {acting === `remove-all-${actingPrefix}` ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Trash2 className="w-2.5 h-2.5" />}
        Remove All ({count})
      </button>
    </div>
  );
}

/* ─── Queue Card (Backfill) ────────────────────── */

function QueueCard({
  title,
  queueName,
  icon: Icon,
  data,
  loading,
  acting,
  onRefresh,
  onRetry,
  onRemove,
  onDrain,
  onRetryAll,
  onRemoveAll,
  onSelectJob,
}: {
  title: string;
  queueName: string;
  icon: React.ElementType;
  data: QueueStatusData | null;
  loading: boolean;
  acting: string | null;
  onRefresh: () => void;
  onRetry: (jobId: string) => void;
  onRemove: (jobId: string) => void;
  onDrain: () => void;
  onRetryAll: () => void;
  onRemoveAll: () => void;
  onSelectJob: (job: QueueJob) => void;
}) {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{title}</span>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="ml-auto text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </button>
      </div>

      {!data ? (
        <div className="p-4 text-center">
          <button onClick={onRefresh} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-400">Load</button>
        </div>
      ) : (
        <div className="p-3 space-y-3">
          <QueueCountRow counts={data.counts} />

          {data.failed.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-red-600 dark:text-red-400">Failed</span>
                <BulkActions
                  count={data.failed.length}
                  acting={acting}
                  actingPrefix={queueName}
                  onRetryAll={onRetryAll}
                  onRemoveAll={onRemoveAll}
                />
              </div>
              <div className="space-y-1.5">
                {data.failed.map((job) => (
                  <FailedJobRow
                    key={job.id}
                    job={job}
                    acting={acting}
                    onRetry={() => onRetry(job.id)}
                    onRemove={() => onRemove(job.id)}
                    onSelect={() => onSelectJob(job)}
                  />
                ))}
              </div>
            </div>
          )}

          {data.active.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-blue-600 dark:text-blue-400 mb-1">Active</div>
              {data.active.map((job) => (
                <div key={job.id} className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-lg p-1.5 flex items-center justify-between">
                  <span className="text-[10px] text-blue-800 dark:text-blue-400 truncate">{job.name}</span>
                  <span className="text-[9px] text-blue-600 dark:text-blue-400">{typeof job.progress === 'number' ? `${job.progress}%` : '...'}</span>
                </div>
              ))}
            </div>
          )}

          {data.waiting.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-amber-600 mb-1">Waiting ({data.counts.waiting})</div>
              {data.waiting.slice(0, 3).map((job) => (
                <div key={job.id} className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 rounded-lg p-1.5 flex items-center justify-between mb-1">
                  <span className="text-[10px] text-amber-800 dark:text-amber-400 truncate">{job.name}</span>
                  <button onClick={() => onRemove(job.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {(data.counts.waiting > 0 || data.counts.failed > 0) && (
            <div className="flex justify-end">
              <button
                onClick={onDrain}
                disabled={acting === `drain-${queueName}`}
                className="text-[10px] text-red-500 hover:text-red-700 dark:hover:text-red-400 flex items-center gap-1"
              >
                {acting === `drain-${queueName}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Drain queue
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Daily Pull Card ──────────────────────────── */

function DailyPullCard({
  detail,
  data,
  loading,
  acting,
  onRefresh,
  onRetry,
  onRemove,
  onDrain,
  onRetryAll,
  onRemoveAll,
  onSelectJob,
}: {
  detail: ConfigDetail;
  data: QueueStatusData | null;
  loading: boolean;
  acting: string | null;
  onRefresh: () => void;
  onRetry: (jobId: string) => void;
  onRemove: (jobId: string) => void;
  onDrain: () => void;
  onRetryAll: () => void;
  onRemoveAll: () => void;
  onSelectJob: (job: QueueJob) => void;
}) {
  const filteredFailed = data?.failed.filter(
    j => j.data?.['storeId'] === detail.storeId && j.data?.['platform'] === detail.platform,
  ) ?? [];

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
        <Activity className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Daily Pull Queue</span>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="ml-auto text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </button>
      </div>

      {!data ? (
        <div className="p-4 text-center">
          <button onClick={onRefresh} className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-400">Load</button>
        </div>
      ) : (
        <div className="p-3 space-y-3">
          <QueueCountRow counts={data.counts} />

          {data.delayed.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-gray-600 dark:text-gray-300 mb-1">Next Scheduled ({data.delayed.length})</div>
              <div className="space-y-1">
                {data.delayed
                  .filter(j => j.data?.['storeId'] === detail.storeId && j.data?.['platform'] === detail.platform)
                  .slice(0, 3)
                  .map((job) => (
                    <div key={job.id} className="bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-1.5 flex items-center justify-between">
                      <span className="text-[10px] text-gray-700 dark:text-gray-200 truncate">{String(job.name).replace('repeat:', '')}</span>
                      <span className="text-[9px] text-gray-500 dark:text-gray-400">
                        {job.timestamp ? new Date(job.timestamp).toLocaleString() : '--'}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {filteredFailed.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-red-600 dark:text-red-400">Failed</span>
                <BulkActions
                  count={filteredFailed.length}
                  acting={acting}
                  actingPrefix="daily-pull"
                  onRetryAll={onRetryAll}
                  onRemoveAll={onRemoveAll}
                />
              </div>
              <div className="space-y-1.5">
                {filteredFailed.map((job) => (
                  <FailedJobRow
                    key={job.id}
                    job={job}
                    acting={acting}
                    onRetry={() => onRetry(job.id)}
                    onRemove={() => onRemove(job.id)}
                    onSelect={() => onSelectJob(job)}
                  />
                ))}
              </div>
            </div>
          )}

          {data.active.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-blue-600 dark:text-blue-400 mb-1">Active</div>
              {data.active.map((job) => (
                <div key={job.id} className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900/50 rounded-lg p-1.5 flex items-center justify-between">
                  <span className="text-[10px] text-blue-800 dark:text-blue-400 truncate">
                    {String(job.data?.['platform'] ?? job.name)} -- store {String(job.data?.['storeId'] ?? '?')}
                  </span>
                  <span className="text-[9px] text-blue-600 dark:text-blue-400">{typeof job.progress === 'number' ? `${job.progress}%` : '...'}</span>
                </div>
              ))}
            </div>
          )}

          {data.completed.length > 0 && (
            <div>
              <div className="text-[10px] font-medium text-green-600 dark:text-green-400 mb-1">Recent Completed</div>
              <div className="space-y-1">
                {data.completed
                  .filter(j => j.data?.['storeId'] === detail.storeId && j.data?.['platform'] === detail.platform)
                  .slice(0, 5)
                  .map((job) => (
                    <div key={job.id} className="bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900/50 rounded-lg p-1.5 flex items-center justify-between">
                      <span className="text-[10px] text-green-700 dark:text-green-400 truncate">
                        {job.finishedOn ? new Date(job.finishedOn).toLocaleString() : '--'}
                      </span>
                      <span className="text-[9px] text-green-600 dark:text-green-400">
                        {job.data?.['timezone'] ? `tz: ${String(job.data['timezone'])}` : ''}
                      </span>
                    </div>
                  ))}
                {data.completed.filter(j => j.data?.['storeId'] === detail.storeId && j.data?.['platform'] === detail.platform).length === 0 && (
                  <div className="text-[9px] text-gray-400 dark:text-gray-500">No completed runs for this config</div>
                )}
              </div>
            </div>
          )}

          {(data.counts.waiting > 0 || data.counts.failed > 0) && (
            <div className="flex justify-end">
              <button
                onClick={onDrain}
                disabled={acting === 'drain-daily-pull'}
                className="text-[10px] text-red-500 hover:text-red-700 dark:hover:text-red-400 flex items-center gap-1"
              >
                {acting === 'drain-daily-pull' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                Drain queue
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
